/**
 * ビューの中の、業務（F3）への導線 — 業務詳細（データの流れの図）へ、業務ルールの画面へ、業務から
 * それを使う業務フローへ。
 *
 * **案件は画面が受け取った番号をそのまま渡す。** 読む場所を画面の入力 1 つに揃える
 * （`common.ts` の docblock）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細へのリンク・業務ルールの画面へのリンク・業務を使う業務フローを持つ。
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 */
import type { BusinessMapFlow } from '@cortex/domain-spec-console';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { displayLabel } from '../../components/spec/UntranslatedBadge.js';
import { pjLinkSearch } from '../pj-link.js';
import { BUSINESS_DETAIL_ROUTE, RULE_ROUTE } from '../route-paths.js';
import { VIEW_LINK_CLASS, pjLinkValue } from './common.js';
import { BusinessFlowLink } from './flow.js';

/**
 * `BusinessDetailLink` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細へのリンクの入力（開く業務の id・引き継ぐ案件番号・中身）
 * @graph-connects none
 */
export interface BusinessDetailLinkProps {
  /** 開く業務の id */
  businessId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
  /**
   * リンクの中身（業務名など）。「業務詳細を開く」の文言だけのリンクは置かない — 見出しは業務名そのものを
   * リンクにした（Dhik 2026-09-29「情報を絞る」）。
   */
  children: ReactNode;
}

/**
 * 業務 1 件の業務詳細へのリンク。案件を引き継いだまま開く。
 *
 * @param props 業務の id・案件番号・中身
 * @returns 業務詳細へのリンク
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件の業務詳細へのリンクを、案件を引き継いだまま描く。
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 */
export function BusinessDetailLink({ businessId, pjNumber, children }: BusinessDetailLinkProps) {
  return (
    <Link
      to={BUSINESS_DETAIL_ROUTE}
      params={{ businessId: String(businessId) }}
      {...pjLinkSearch(pjLinkValue(pjNumber))}
      className={VIEW_LINK_CLASS}
    >
      {children}
    </Link>
  );
}

/**
 * `RuleLink` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの画面へのリンクの入力（開く業務ルールの id・引き継ぐ案件番号・中身）
 * @graph-connects none
 */
export interface RuleLinkProps {
  /** 開く業務ルールの id */
  ruleId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
  /** リンクの中身 */
  children: ReactNode;
}

/**
 * 業務ルール 1 件の画面へのリンク。業務ルールは業務から独立しているので、持ち主の業務を決めずに開ける。
 *
 * @param props 業務ルールの id・案件番号・中身
 * @returns 業務ルールの画面へのリンク
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件の画面へのリンクを、案件を引き継いだまま描く。
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 */
export function RuleLink({ ruleId, pjNumber, children }: RuleLinkProps) {
  return (
    <Link
      to={RULE_ROUTE}
      params={{ ruleId: String(ruleId) }}
      {...pjLinkSearch(pjLinkValue(pjNumber))}
      className={VIEW_LINK_CLASS}
      data-rule-link={ruleId}
    >
      {children}
    </Link>
  );
}

/**
 * 業務 1 件を使っている業務フローを引く。
 *
 * 業務から業務フローへの参照は持たない。業務マップが返すのは業務フロー側の `businessIds[]`
 * だけで（設計書 `api.html#a5-1` 4.1）、逆向きの列を足すと同じ事実が 2 箇所に出る。
 *
 * @param flows 業務マップの業務フローの並び
 * @param businessId 使われている側の業務の id
 * @returns その業務を使っている業務フロー。無ければ空
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件を使っている業務フローを、業務フロー側の参照から引く。
 * @graph-connects none
 */
export function flowsUsingBusiness(
  flows: readonly BusinessMapFlow[],
  businessId: number,
): readonly BusinessMapFlow[] {
  return flows.filter((flow) => flow.businessIds.includes(businessId));
}

/**
 * `BusinessUsedByFlows` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務を使っている業務フローの並びの入力（業務マップの業務フロー・読んでいる業務の id・引き継ぐ案件番号）
 * @graph-connects none
 */
export interface BusinessUsedByFlowsProps {
  /** 業務マップの業務フローの並び */
  flows: readonly BusinessMapFlow[];
  /** いま読んでいる業務の id */
  businessId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
}

/**
 * 業務詳細（データの流れの図）の見出しに添える、その業務を使っている業務フローへのリンク。
 *
 * **業務は 1 本の流れの中の 1 段である。** どの流れから呼ばれるのかを同じ見出しで
 * 出さないと、読んでいる業務が仕様のどこに載っているのかを確かめるために一度上の画面へ
 * 戻ることになる。
 *
 * 使っている業務フローが無いときも黙らない。「登録はされているがどの流れからも呼ばれて
 * いない」は実データの穴そのもので、何も描かないと穴が画面から消える。
 *
 * @param props 業務フローの並び・業務の id・案件番号
 * @returns その業務を使っている業務フローへのリンクの列
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細の見出しに、その業務を使っている業務フローへのリンクを並べる。
 * @graph-connects flowsUsingBusiness [calls] 業務を使っている業務フローの引き当て
 * @graph-connects BusinessFlowLink [calls] 業務フロー 1 本の図へのリンクの描画
 */
export function BusinessUsedByFlows({ flows, businessId, pjNumber }: BusinessUsedByFlowsProps) {
  const { t } = useTranslation();
  const usedBy = flowsUsingBusiness(flows, businessId);

  if (usedBy.length === 0) {
    return (
      <p data-used-by-flows="0" className="text-xs text-[var(--color-text-muted)]">
        {t('viewLinks.usedByFlowsEmpty')}
      </p>
    );
  }

  return (
    <p
      data-used-by-flows={usedBy.length}
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-muted)]"
    >
      <span>{t('viewLinks.usedByFlows')}</span>
      {usedBy.map((flow) => (
        <BusinessFlowLink key={flow.flowId} flowId={flow.flowId} pjNumber={pjNumber}>
          {displayLabel(flow.label, t, flow.untranslated)}
        </BusinessFlowLink>
      ))}
    </p>
  );
}
